import { useEffect } from 'react';
import { lenteBloccata } from './useBloccoLente';
import {
  distanza,
  pinchAvviato,
  puntoMedio,
  scalaLimitata,
  spostamento,
  trasformazione,
  type Punto,
} from '../utils/lente-math';

/**
 * Il pinch-to-zoom come una LENTE: ingrandisce finché tieni le dita, e al
 * rilascio la vista torna da sola dov'era.
 *
 * Il motivo per cui esiste. Il meta viewport dichiarava
 * `maximum-scale=1.0, user-scalable=no` — una barriera d'accesso, WCAG 1.4.4 — e
 * toglierlo ha restituito a tutti lo zoom nativo del browser. Solo che lo zoom
 * nativo RESTA: in un'app a schermo pieno, chi allargava due dita per leggere
 * una casella si ritrovava l'interfaccia tagliata e nessuna uscita ovvia se non
 * un secondo pinch fatto bene. La lente dà l'ingrandimento senza lo stato
 * appiccicoso: si guarda, si molla, si è tornati.
 *
 * ⚠️ Il meta viewport NON si tocca, e non è un dettaglio: rimettere
 * `user-scalable=no` sarebbe tornare alla barriera d'accesso appena rimossa (e
 * `src/__tests__/zoom-e-campi-di-testo.test.ts` lo vieta). Lo zoom nativo si
 * spegne dove il tocco comincia, non a livello di documento:
 *  - Android/Chrome — `touch-action: pan-x pan-y` (index.css), e in DUE regole,
 *    non una: su `#root`, e sui figli di `body` che non sono `#root`. La
 *    seconda non è una ridondanza — i portali (`createPortal(…, document.body)`)
 *    stanno ACCANTO a `#root`, non dentro, e per un pezzo d'app montato lì
 *    `#root` non è fra gli antenati. Con la sola prima regola, dentro ogni
 *    overlay portato il pinch nativo restava vivo;
 *  - iOS Safari — `preventDefault()` su `gesturestart`/`gesturechange`, perché
 *    Safari ignora `touch-action` per lo zoom di pagina. In PWA installata lo
 *    zoom di pagina è già spento dal sistema, quindi lì è una rete di sicurezza.
 * Lo zoom da tastiera e da rotella su desktop resta intatto: è l'uscita di
 * sicurezza per chi ha davvero bisogno di un ingrandimento STABILE, e la lente
 * non gliela toglie.
 *
 * Un solo proprietario per tutta l'app — come `useBackNavigation` lo è di
 * `popstate` — chiamato una volta sola da `App.tsx`. Dove la scacchiera occupa
 * tutto lo schermo la lente non fa nulla: vedi `useBloccoLente`.
 */

/** Quanto ci mette la molla a riportare la vista al punto di partenza. */
const DURATA_RITORNO_MS = 200;

interface Gesto {
  distanzaIniziale: number;
  centroIniziale: Punto;
  /** Il pinch è cominciato davvero (dita mosse oltre la soglia), non solo appoggiate. */
  avviato: boolean;
}

function puntiDelTocco(tocchi: TouchList): [Punto, Punto] {
  const a = tocchi[0]!;
  const b = tocchi[1]!;
  return [
    { x: a.clientX, y: a.clientY },
    { x: b.clientX, y: b.clientY },
  ];
}

function ritornoImmediato(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * L'elemento che si trasforma è `<html>`, e la scelta è OBBLIGATA.
 *
 * Un antenato con `transform` diventa il blocco contenitore dei discendenti
 * `position: fixed`: da quel momento non sono più ancorati alla viewport ma al
 * padding box dell'antenato, e quindi SCORRONO con il contenuto. Su `#root` —
 * dov'era la prima versione — la barra di navigazione in basso saliva in mezzo
 * allo schermo, sopra il contenuto: `#root` è alto una viewport e vive dentro
 * l'area che scorre, quindi al suo interno `bottom: 0` non è il fondo dello
 * schermo ma il fondo di `#root`, che a scorrimento S sta S pixel più in alto.
 * Si vedeva solo da loggato, perché la dashboard e' la schermata alta da
 * scorrere: a pagina non scorsa S vale 0 e non succede niente.
 *
 * `body` non risolve, e vale la pena saperlo prima di riprovarci: e' LUI il
 * contenitore di scorrimento (`html, body { height: 100% }` piu' `overflow-x:
 * hidden` gli fanno calcolare `overflow-y: auto`), quindi i suoi discendenti
 * fissi diventano assoluti dentro un contenuto che e' scorso, ed e' lo stesso
 * spostamento. Misurato a 360px con `body.scrollTop = 400` e `scale(2)` attorno
 * a y=400, con la barra a riposo in `[736, 800]`: su `#root` e su `body`
 * `[272, 400]`, su `<html>` `[1072, 1200]` — cioe' il valore giusto,
 * `400 + (736-400)*2`.
 *
 * `<html>` funziona perche' e' l'unico che NON ha uno scorrimento proprio
 * (`scrollHeight === clientHeight`): il suo padding box coincide sempre con la
 * viewport. L'invariante e' la stessa su cui si appoggia gia' il salvataggio
 * dello scorrimento in `App.tsx`, ed e' sorvegliata da
 * `useLente.cablaggio.test.ts`.
 *
 * Come effetto collaterale l'origine si calcola in coordinate di VIEWPORT: il
 * rettangolo di `<html>` parte da (0, 0).
 */
export function useLente(): void {
  useEffect(() => {
    const radice = document.documentElement;

    let gesto: Gesto | null = null;
    /* Uno scorrimento a un dito già avviato non si può più annullare: il browser
       se l'è preso, e `preventDefault` su un `touchmove` non annullabile non
       ferma niente. Se il secondo dito arriva DOPO che la pagina ha cominciato a
       scorrere, si lascia perdere il gesto — meglio nessuna lente che una lente
       che scivola insieme allo scorrimento. */
    let scorrimentoInCorso = false;
    let frame = 0;
    let timerRitorno: ReturnType<typeof setTimeout> | undefined;

    const pulisci = (): void => {
      radice.style.removeProperty('transform');
      radice.style.removeProperty('transform-origin');
      radice.style.removeProperty('transition');
    };

    const preparaRadice = (centro: Punto): void => {
      if (timerRitorno !== undefined) {
        clearTimeout(timerRitorno);
        timerRitorno = undefined;
      }
      pulisci();
      const r = radice.getBoundingClientRect();
      /* L'origine sta sotto le dita: quello che si sta guardando cresce dov'è,
         invece di scappare verso un angolo. */
      radice.style.transformOrigin = `${centro.x - r.left}px ${centro.y - r.top}px`;
      /* Niente `will-change: transform`: promuovere la radice a livello
         composito la congelerebbe alla rasterizzazione a 1×, e un testo piccolo
         ingrandito e sfocato è precisamente ciò che la lente dovrebbe far
         leggere. Senza, il browser ri-rasterizza quando la scala si ferma. */
    };

    const ritorna = (): void => {
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      if (ritornoImmediato()) {
        pulisci();
        return;
      }
      radice.style.transition = `transform ${DURATA_RITORNO_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
      radice.style.transform = 'translate(0px, 0px) scale(1)';
      /* Un timer e non `transitionend`: se il gesto finisce già a scala 1 la
         transizione non parte, l'evento non arriva mai e gli stili in linea
         resterebbero addosso alla radice. E gli stili in linea NON sono
         cosmetici — un `transform` residuo su `<html>` terrebbe ogni
         `position: fixed` ancorato a lui per sempre, e la barra di navigazione,
         i banner e ogni `.modal-overlay` sono montati su ogni ramo dell'app. */
      timerRitorno = setTimeout(() => {
        timerRitorno = undefined;
        pulisci();
      }, DURATA_RITORNO_MS + 40);
    };

    /* Si lascia il gesto e, se aveva gia' ingrandito, si fa tornare la vista.
       Senza il ritorno, un TERZO dito appoggiato a meta' pinch congelerebbe la
       pagina all'ultima scala: `gesto` sparisce, e il `touchend` che dovrebbe
       far scattare la molla non trova piu' niente da riportare indietro. */
    const abbandona = (): void => {
      if (gesto?.avviato) ritorna();
      gesto = null;
    };

    const suInizio = (e: TouchEvent): void => {
      if (e.touches.length === 1) {
        scorrimentoInCorso = false;
        return;
      }
      if (e.touches.length !== 2 || lenteBloccata() || scorrimentoInCorso) {
        abbandona();
        return;
      }
      const [a, b] = puntiDelTocco(e.touches);
      gesto = { distanzaIniziale: distanza(a, b), centroIniziale: puntoMedio(a, b), avviato: false };
    };

    const suMovimento = (e: TouchEvent): void => {
      if (!gesto) {
        if (e.touches.length === 1) scorrimentoInCorso = true;
        return;
      }
      if (e.touches.length !== 2) return;

      const [a, b] = puntiDelTocco(e.touches);
      const d = distanza(a, b);
      if (!gesto.avviato) {
        if (!pinchAvviato(d, gesto.distanzaIniziale)) return;
        gesto.avviato = true;
        preparaRadice(gesto.centroIniziale);
      }
      /* Ferma lo scorrimento sotto le dita: senza, la pagina si muoverebbe e si
         ingrandirebbe insieme. Richiede `{ passive: false }` in registrazione. */
      if (e.cancelable) e.preventDefault();

      const scala = scalaLimitata(d, gesto.distanzaIniziale);
      const { dx, dy } = spostamento(gesto.centroIniziale, puntoMedio(a, b));
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = 0;
        radice.style.transform = trasformazione({ scala, dx, dy });
      });
    };

    const suFine = (e: TouchEvent): void => {
      if (e.touches.length === 0) scorrimentoInCorso = false;
      if (e.touches.length >= 2) return;
      abbandona();
    };

    /* Safari: `gesturestart` è l'unico punto in cui lo zoom di pagina si può
       rifiutare — `touch-action` lì non lo governa. */
    const bloccaGestoIOS = (e: Event): void => {
      if (e.cancelable) e.preventDefault();
    };

    const nonPassivo = { passive: false } as const;
    document.addEventListener('touchstart', suInizio, nonPassivo);
    document.addEventListener('touchmove', suMovimento, nonPassivo);
    document.addEventListener('touchend', suFine, nonPassivo);
    document.addEventListener('touchcancel', suFine, nonPassivo);
    for (const nome of ['gesturestart', 'gesturechange', 'gestureend']) {
      document.addEventListener(nome, bloccaGestoIOS, nonPassivo);
    }

    return () => {
      document.removeEventListener('touchstart', suInizio);
      document.removeEventListener('touchmove', suMovimento);
      document.removeEventListener('touchend', suFine);
      document.removeEventListener('touchcancel', suFine);
      for (const nome of ['gesturestart', 'gesturechange', 'gestureend']) {
        document.removeEventListener(nome, bloccaGestoIOS);
      }
      if (frame) cancelAnimationFrame(frame);
      if (timerRitorno !== undefined) clearTimeout(timerRitorno);
      pulisci();
    };
  }, []);
}
